import React from "react";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Form } from "@dreambigger/design-system/src/components";
import { StepProps } from "../pages/flows/[flowId]";
import StepWrapper from "./step-wrapper";
import { useApplication, useCrossSell } from "../api";
import { RadioCardFormItem } from "@dreambigger/shared/src/components";
import type { RadioCardFormItemProps } from "@dreambigger/shared/src/components/RadioCardFormItem";
import { Spin } from "antd";
import { Loading3QuartersOutlined } from "@ant-design/icons";
import {
  CrossSellStepAssets,
  ResponseFields,
  ResponseStep,
} from "@dreambigger/shared/src/types";
import useNextStep from "../hooks/use-next-step";
import { changeStepForward } from "../utils/changeStepForward";
export interface FlowOfferData {
  uuid: string;
  slug: string;
  financial_institution_product_id: string;
  flow_id?: string | null;
  share_product_uuid?: string;
  display_order?: number;
  offer_instance_uuid?: string;
  meta: {
    title?: string;
    description?: string;
    heroText?: string;
    modal?: {
      ctaText?: string;
      title?: string;
      description?: string;
    };
    defaultValue?: boolean;
  };
  shareProduct?: {
    uuid: string;
    title: string;
    description?: string;
  };
}

const spinnerIcon = <Loading3QuartersOutlined spin />;

// Offer Option Configuration
const OPTIONS_LABEL = "Are you interested?";
const OPTIONS: RadioCardFormItemProps["options"] = [
  {
    label: "Yes",
    description: "Yes, I am interested.",
    value: true,
    type: "yes",
  },
  {
    label: "No",
    description: "No, I am not interested at this time.",
    value: false,
    type: "no",
  },
];

const CrossSell = ({ flow, step, brand, progress }: StepProps) => {
  const assets: CrossSellStepAssets = step.assets;
  const { maxOffers } = assets;

  const [disabled, setDisabled] = useState(true);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [form] = Form.useForm();
  const applicationHelper = useApplication(flow.financialInstitution.id);
  const application = useMemo(
    () => applicationHelper.find(flow.id, "draft"),
    [applicationHelper, flow]
  );
  // Which offers match is decided server-side from the application's current
  // responses, so the cached verdict is only good while those responses are. The
  // alternative — a URL-only key — survives a cart change and makes the step
  // replay a stale (often empty) offer list, which reads as "it skipped again".
  const offersCacheKey = useMemo(
    () =>
      application?.responses
        ? JSON.stringify(
            application.responses.map((r: ResponseStep) => [r.slug, r.fields])
          )
        : "",
    [application]
  );
  const crossSellData = useCrossSell(
    application?.id,
    flow.financialInstitution.id,
    maxOffers, // API defaults to 1 if not provided (see crossSellController.ts)
    offersCacheKey
  );
  const response = useMemo(
    () => applicationHelper.findResponse(flow.id, "draft", step.slug),
    [application, flow, step]
  );
  const nextStep = useNextStep(assets, applicationHelper, flow, step.slug);

  useEffect(() => {
    form.resetFields();
  }, [step, response]);

  // Get flow offers (already sorted and limited by API)
  const flowOffers = useMemo(() => {
    if (!crossSellData.data) return [];
    return crossSellData.data;
  }, [crossSellData.data]);

  // antd applies initial values only on mount/reset, so reset once when the offers first arrive (for `defaultValue`).
  const prefilledForStep = useRef<string | null>(null);
  useEffect(() => {
    if (!flowOffers.length || prefilledForStep.current === step.slug) return;
    prefilledForStep.current = step.slug;
    if (!form.isFieldsTouched()) form.resetFields();
  }, [flowOffers, step.slug, form]);

  // Pre-select offers that carry a `defaultValue`; saved answers win.
  //
  // Interest mode only. `defaultValue` was designed when a "yes" recorded
  // interest and nothing else; under add/replace the same pre-tick, combined with
  // a Continue that is enabled on arrival, would add a product — and in `replace`
  // remove one the applicant chose — without them touching anything. Opting
  // someone into a financial product by default is a decision for an affirmative
  // click, not a config flag.
  const handlePrefill = useCallback(
    (initialValues?: ResponseFields) => {
      const values: ResponseFields = { ...initialValues };
      if (assets.mode === "add" || assets.mode === "replace") {
        // The card is only served while its product is not in the cart, so a
        // saved "yes" means the applicant since removed the product: ask again.
        // Done on every key (not just loaded offers) so the form never mounts
        // with the stale answer before the offers arrive.
        for (const key of Object.keys(values)) {
          if (values[key] === true) delete values[key];
        }
        return values;
      }
      for (const offer of flowOffers) {
        if (offer.meta.defaultValue && values[offer.slug] === undefined) {
          values[offer.slug] = true;
        }
      }
      return values;
    },
    [flowOffers, assets.mode]
  );

  const checkDisabled = (values?: any) => {
    // Do not allow user to continue if still loading
    if (crossSellData.isValidating) {
      setDisabled(true);
      return;
    }

    // Allow user to continue if no offers are presented
    if (!flowOffers.length) {
      setDisabled(false);
      return;
    }

    // Check if any offers are missing responses
    const hasAnyMissingResponses = flowOffers.some((offer) => {
      const fieldValue = values?.[offer.slug];
      return (
        fieldValue === undefined || fieldValue === null || fieldValue === ""
      );
    });

    setDisabled(hasAnyMissingResponses);
  };

  // Automatically advance to the next step if offers are not presented
  useEffect(() => {
    if (
      !crossSellData.isValidating &&
      !flowOffers.length &&
      application &&
      !hasNavigated &&
      applicationHelper &&
      nextStep
    ) {
      setHasNavigated(true);
      if (assets.runTransitionRules) {
        applicationHelper
          .getNextStep({
            applicationId: application.id,
            stepSlug: step.slug,
            transformUUID:
              typeof assets.runTransitionRules === "object"
                ? assets.runTransitionRules.transformUUID
                : undefined,
          })
          .then(({ data }) =>
            data.nextStep ? changeStepForward(data.nextStep) : nextStep({})
          )
          .catch((error) => {
            console.error("Failed to navigate to next step:", error);
            setHasNavigated(false);
          });
      } else {
        nextStep({});
      }
    }
  }, [
    crossSellData.isValidating,
    flowOffers,
    application,
    hasNavigated,
    applicationHelper,
    nextStep,
    assets.runTransitionRules,
    step.slug,
    form,
  ]);

  // Check disabled state when form values change
  useEffect(() => {
    if (!crossSellData.isValidating) {
      checkDisabled(form.getFieldsValue());
    }
  }, [form, flowOffers]);

  const wrapperProps = {
    step,
    brand,
    flow,
    progress,
    form,
    disabled,
    handlePrefill,
  };

  // Return nothing if no application is found
  if (!application) {
    return null;
  }

  // Show spinner while loading offers. Override title/description so that normal step assets will only display when offers are present, allowing for better marketing.
  if (crossSellData.isValidating || !flowOffers.length) {
    const loadingWrapperProps = {
      ...wrapperProps,
      step: {
        ...step,
        assets: {
          ...step.assets,
          title: "Reviewing application...",
          description: "",
        },
      },
    };
    return (
      <StepWrapper {...loadingWrapperProps}>
        <Spin indicator={spinnerIcon} />
      </StepWrapper>
    );
  }

  return (
    <StepWrapper {...wrapperProps}>
      {flowOffers.map((offer: FlowOfferData) => {
        const title = offer.meta.title || offer.shareProduct?.title || "";
        const description =
          offer.meta.description || offer.shareProduct?.description || "";
        return (
          <RadioCardFormItem
            key={offer.slug}
            className="mb-2"
            title={title}
            titleColor={brand.stepTitleColor}
            description={description}
            callout={offer.meta.heroText}
            modalLaunchText={offer.meta.modal?.ctaText}
            modalTitle={offer.meta.modal?.title}
            modalContent={offer.meta.modal?.description}
            optionsLabel={OPTIONS_LABEL}
            options={OPTIONS}
            form={form}
            fieldName={offer.slug}
            required={true}
            onChange={() => checkDisabled(form.getFieldsValue())}
            segmentProperties={{
              financialInstitutionId: flow.financialInstitution.id,
              offerUuid: offer.uuid,
              offerSlug: offer.slug,
              offerInstanceUuid: offer.offer_instance_uuid,
              shareProductUuid: offer.share_product_uuid,
              // flowId and stepSlug are also automatically added
            }}
          />
        );
      })}
    </StepWrapper>
  );
};

export default CrossSell;
